/**
 * @fileoverview useThemeMode - Theme Mode Management Hook
 *
 * Manages dark/light mode preference with:
 * - System preference detection (prefers-color-scheme)
 * - Manual override (light/dark/system)
 * - localStorage persistence
 * - Cross-tab synchronization
 * - Smooth transitions
 *
 * @module hooks/useThemeMode
 */

import { useCallback, useEffect, useState } from 'react';

import { logger } from '../utils/logger';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedMode = 'light' | 'dark';

export const THEME_MODE_STORAGE_KEY = 'theme-mode';

interface UseThemeModeReturn {
  /** Current mode setting (light/dark/system) */
  mode: ThemeMode;
  /** Resolved actual mode (light/dark) - resolves 'system' */
  resolvedMode: ResolvedMode;
  /** Convenience boolean for dark mode */
  isDark: boolean;
  /** Set the theme mode */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark */
  toggle: () => void;
}

/**
 * Get system color scheme preference
 */
const getSystemPreference = (): ResolvedMode => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Resolve 'system' mode to actual light/dark
 */
const resolveMode = (mode: ThemeMode): ResolvedMode => {
  if (mode === 'system') {
    return getSystemPreference();
  }
  return mode;
};

/**
 * Load mode from localStorage
 */
const loadModeFromStorage = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system';

  try {
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch (error) {
    logger.error('[useThemeMode] Error loading from localStorage:', error);
  }

  return 'system';
};

/**
 * Save mode to localStorage
 */
const saveModeToStorage = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    logger.debug('[useThemeMode] Mode saved:', mode);
  } catch (error) {
    logger.error('[useThemeMode] Error saving to localStorage:', error);
  }
};

/**
 * Apply theme mode to document
 */
const applyModeToDocument = (resolved: ResolvedMode): void => {
  const root = document.documentElement;

  // Add transitioning class to prevent flash
  root.classList.add('theme-transitioning');

  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // Remove transitioning class after a frame
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.classList.remove('theme-transitioning');
    });
  });

  logger.debug('[useThemeMode] Applied mode to document:', resolved);
};

/**
 * Hook to manage theme mode (light/dark/system)
 *
 * @returns Theme mode state and controls
 *
 * @example
 * function ThemeToggle() {
 *   const { isDark, toggle, mode, setMode } = useThemeMode();
 *
 *   return (
 *     <button onClick={toggle}>
 *       {isDark ? 'Dark' : 'Light'}
 *     </button>
 *   );
 * }
 */
export const useThemeMode = (): UseThemeModeReturn => {
  const [mode, setModeState] = useState<ThemeMode>(() => loadModeFromStorage());
  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() =>
    resolveMode(loadModeFromStorage())
  );

  // Apply mode changes
  useEffect(() => {
    const newResolved = resolveMode(mode);
    setResolvedMode(newResolved);
    applyModeToDocument(newResolved);
  }, [mode]);

  // Listen for system preference changes
  useEffect(() => {
    if (mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const newResolved = e.matches ? 'dark' : 'light';
      setResolvedMode(newResolved);
      applyModeToDocument(newResolved);
      logger.debug('[useThemeMode] System preference changed:', newResolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === THEME_MODE_STORAGE_KEY && e.newValue) {
        const newMode = e.newValue as ThemeMode;
        if (newMode === 'light' || newMode === 'dark' || newMode === 'system') {
          setModeState(newMode);
          logger.debug('[useThemeMode] Mode synced from other tab:', newMode);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    saveModeToStorage(newMode);
  }, []);

  const toggle = useCallback(() => {
    const newMode = resolvedMode === 'dark' ? 'light' : 'dark';
    setMode(newMode);
  }, [resolvedMode, setMode]);

  return {
    mode,
    resolvedMode,
    isDark: resolvedMode === 'dark',
    setMode,
    toggle,
  };
};

export default useThemeMode;
